import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { isOffline } from "../lib/offlineStore";
import { ROUND_STRENGTH } from "../engine/layout";
import { useWorkspace } from "../store/WorkspaceContext";

// Knife speed / force / passes … and the chosen plotter model follow the ACCOUNT (server), not the browser session:
// load them with the machine list on start, keep them through plotter auto-detect, and save every change.
export function useCutSettings(userId) {
  const { state, dispatch } = useWorkspace();
  const timer = useRef(null);
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    dispatch({ type: "SET", patch: { cutSettingsLoaded: false, cutSettingsTouched: false } });
    localStorage.removeItem("xpattern.calibration"); // stale per-browser values used to override the account setting
    Promise.all([api.machines().catch(() => []), api.plotters().catch(() => ({}))]).then(([ms, r]) => {
      if (!alive) return;
      const cs = r.cut_settings;
      const saved = cs?.settings && Object.keys(cs.settings).length ? cs.settings : null;
      const patch = { machines: ms, savedCutSettings: saved, savedMachine: cs?.machine_id ? { machine_id: cs.machine_id, pinned: Boolean(cs.pinned) } : null };
      if (r.calibration?.scale_width) patch.calibration = r.calibration;
      dispatch({ type: "SET", patch });
      // Corner radius follows the account: apply it here; a browser-only choice from before is lifted onto the account once.
      const cr = r.prefs?.corner_round;
      if (cr && ROUND_STRENGTH[cr] !== undefined) { if (cr !== state.autoRound) dispatch({ type: "SET_AUTO_ROUND", value: cr }); }
      else if (!isOffline() && state.autoRound !== "off" && ROUND_STRENGTH[state.autoRound] !== undefined) api.savePrefs({ corner_round: state.autoRound, corner_round_frameless: state.framelessRound }).catch(() => {});
      const fr = r.prefs?.corner_round_frameless;  // Tesla / frameless glass has its own small radius on the account
      if (fr && ["off", "x64", "hair", "tiny", "xsmall"].includes(fr) && fr !== state.framelessRound) dispatch({ type: "SET_FRAMELESS_ROUND", value: fr });
      const remembered = cs?.machine_id && ms.find((m) => m.id === cs.machine_id);
      const def = remembered || ms.find((m) => m.brand === "Generic" && m.command_format === "hpgl") || ms[0];
      if (def) dispatch({ type: "SELECT_MACHINE", machine: def, extra: { machinePinned: Boolean(remembered && cs.pinned), machineSource: null } });
      dispatch({ type: "SET", patch: { cutSettingsLoaded: true } });
    });
    return () => { alive = false; };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const { machineSettings, machine, machinePinned, machineSource, cutSettingsLoaded, cutSettingsTouched, savedCutSettings } = state;
  useEffect(() => {
    if (!userId || !cutSettingsLoaded) return;
    // Save once the customer changed something, or the moment a plotter is detected: its profile (speed, force, offsets …)
    // is locked to the account right there, so every later cut — any session, any computer — uses the same values.
    if (!(cutSettingsTouched || savedCutSettings || machineSource)) return; // never lock in the generic defaults nobody chose
    const firstLock = !savedCutSettings && !cutSettingsTouched && Boolean(machineSource);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const body = { machine_id: machine?.id || null, pinned: Boolean(machinePinned), settings: machineSettings };
      api.saveCutSettings(body).then(() => {
        dispatch({ type: "SET", patch: { savedCutSettings: machineSettings, savedMachine: { machine_id: body.machine_id, pinned: body.pinned } } });
        if (firstLock && machine) toast.success(`${machine.brand} ${machine.model} detected — its cut settings (speed ${machineSettings.speed}, force ${machineSettings.force}) are now saved to this account`, { duration: 8000 });
      }).catch(() => {});
    }, 600);
    return () => clearTimeout(timer.current);
  }, [machineSettings, machine?.id, machinePinned, machineSource, cutSettingsLoaded, userId]); // eslint-disable-line react-hooks/exhaustive-deps
}
